'use client';

import { c as _c } from "react/compiler-runtime";
import { BasicTooltip } from '@/comps/basic-tooltip';
import { PowerBar } from '@/domains/pages/ai-proficiency/components/power-bar';
import type { OrgScoreBenchmarks } from '@/domains/pages/ai-proficiency/fixtures/benchmarks';
import type { OrgScoreSnapshot } from '@/domains/pages/ai-proficiency/utils/map-aggregates';
import type { TimeRangeKind } from '@/api/generated/model';
import { ProficiencyTrendChip } from '@/domains/pages/ai-proficiency/components/proficiency-trend-chip';
import { BENCHMARK_DESCRIPTION, BENCHMARK_DOCS_URL, BENCHMARK_READ_MORE_LABEL, BENCHMARK_SOURCE_TEXT, SCORE_DESCRIPTION } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { Info } from 'lucide-react';
type AiProficiencyScoreCardProps = {
  orgScore: OrgScoreSnapshot;
  benchmarks: OrgScoreBenchmarks | null;
  timeRangeKind: TimeRangeKind;
};
export function AiProficiencyScoreCard(t0) {
  const $ = _c(22);
  const {
    orgScore,
    benchmarks,
    timeRangeKind
  } = t0;
  const delta = orgScore.delta;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("shrink-0 text-[44px] leading-none font-medium tracking-tight", AIFI_DATA_NUMBER_CLASS);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== orgScore.score) {
    t2 = formatScore(orgScore.score, {
      decimals: 1
    });
    $[1] = orgScore.score;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-2xl text-muted-foreground">/100</span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <span className={t1}>{t2}{t3}</span>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== delta || $[7] !== timeRangeKind) {
    t5 = delta != null ? <ProficiencyTrendChip delta={delta} timeRangeKind={timeRangeKind} /> : null;
    $[6] = delta;
    $[7] = timeRangeKind;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="flex w-full min-w-0 flex-wrap items-end gap-x-3 gap-y-2">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className="mt-3 text-sm leading-snug text-muted-foreground">{SCORE_DESCRIPTION}</p>;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== benchmarks) {
    t8 = benchmarks ? <p className="mt-2 text-sm leading-snug text-muted-foreground">{BENCHMARK_SOURCE_TEXT}{" "}<BasicTooltip text={BENCHMARK_DESCRIPTION} side="bottom" classNameContent="max-w-80 break-normal leading-relaxed"><a href={BENCHMARK_DOCS_URL} target="_blank" rel="noopener noreferrer" aria-label={BENCHMARK_READ_MORE_LABEL} className="inline-flex align-middle text-muted-foreground transition-colors hover:text-foreground"><Info className="size-4" aria-hidden={true} /></a></BasicTooltip></p> : null;
    $[13] = benchmarks;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== benchmarks || $[16] !== orgScore.score) {
    t9 = benchmarks ? <PowerBar className="mt-6" score={orgScore.score} median={benchmarks.median} topQuartile={benchmarks.topQuartile} /> : <PowerBar className="mt-6" score={orgScore.score} />;
    $[15] = benchmarks;
    $[16] = orgScore.score;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t6 || $[19] !== t8 || $[20] !== t9) {
    t10 = <div data-testid="ai-proficiency-score-card">{t6}{t7}{t8}{t9}</div>;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}